/**
 * サーバー推論（pose-server 経由）で画像からポーズを生成するための補助関数。
 * 動画からのポーズ生成パイプライン（poseSolver.ts）とは入力経路が異なるだけで、
 * 出力（Map<VRMHumanBoneName, THREE.Quaternion>）は共通のため、以降の下流
 * （プレビュー・exportPoseVrma 等）はそのまま流用できる。
 */
import { generatePoseFromImage, fetchPoseServerHealth, PoseServerUnavailableError } from '../api/client';
import type { PoseInferenceResult, SomaPose } from '../api/client';
import { mergeMultiViewPoses, toPoseMap, type PoseQuat, type ServerPoseResult } from './multiViewPose';

// mergeMultiViewPoses・toPoseMap・ServerPoseResult・PoseQuat の実体は multiViewPose.ts
// （DOM/api-client非依存の純粋モジュール）にある。pose-factory（独立パッケージ）がそちらを
// 直接importして参照するため分離した（詳細は multiViewPose.ts 冒頭のコメント参照）。この
// ファイルの既存の import 元（Upload.tsx 等）を変えずに済むよう、ここで re-export する。
export { mergeMultiViewPoses, toPoseMap };
export type { PoseQuat, ServerPoseResult };

const DEFAULT_MAX_EDGE = 1280;
const JPEG_QUALITY = 0.85;

export interface DownscaledImage {
  base64: string;
  mimeType: string;
}

/** Blob を data:...;base64,... の base64 部分だけ取り出す。 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('画像の読み込みに失敗しました'));
    reader.readAsDataURL(blob);
  });
}

/**
 * サーバー推論に送る画像を作る。長辺を maxEdge まで縮小し（拡大はしない）、
 * JPEG 0.85 に変換して base64 化する。EXIF の回転情報は createImageBitmap の
 * imageOrientation: 'from-image' で正規化してから描画するため、送信する画像は
 * 見た目通りの向きになる（EXIF タグは再度付与されない）。
 */
export async function downscaleImage(file: File, maxEdge = DEFAULT_MAX_EDGE): Promise<DownscaledImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D コンテキストの取得に失敗しました');
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) throw new Error('画像の変換に失敗しました');

    const base64 = await blobToBase64(blob);
    return { base64, mimeType: 'image/jpeg' };
  } finally {
    bitmap.close();
  }
}

/**
 * generatePoseFromImage の結果から、複数画像アニメーション生成（startPoseAnimation）に渡す
 * キーフレーム（SomaPose）を取り出す。meta.somaPose は旧バックエンド（未対応）では省略されるため、
 * その場合は「サーバーが未対応です」という主旨の分かるエラーにする。
 */
export function extractSomaPose(result: PoseInferenceResult): SomaPose {
  const somaPose = result.meta?.somaPose;
  if (!somaPose) {
    throw new Error(
      'このサーバーは複数画像からのアニメーション生成に未対応です（somaPose が返されませんでした）。',
    );
  }
  return somaPose;
}

// ---- pose-server の自動起動待ち ----------------------------------------------------------

/** 起動待ちポーリングの間隔。5秒おきに fetchPoseServerHealth() で state を確認する。 */
const POLL_INTERVAL_MS = 5_000;
/** 起動待ちの最大許容時間（5分）。初回のモデル読み込みでもここまでには収まる想定。 */
const MAX_WAIT_MS = 5 * 60_000;

/**
 * 画像1枚からのポーズ推定を、pose-server の自動起動待ちを含めて自動で完結させる。
 *
 * 自宅GPU機の推論サーバーは省電力運用（画像が届いたら自動起動、5分アイドルで自動停止）の
 * ため、停止中に最初の画像を送ると supervisor が起動を仕掛けつつ 503（PoseServerUnavailableError,
 * state: 'starting'）を返す。起動には通常1分ほど、初回のモデル読み込み時はもう少しかかる。
 * 従来はこの503をそのままエラー表示していたが、「待てば使えるようになる」ことが分かっている
 * ケースなので、ここで fetchPoseServerHealth() をポーリングして running になるのを待ち、
 * 生成まで自動で続行する。
 *
 * 複数画像を順番に推定するフロー（handleEstimateImage / handleGenerateAnimation）から呼ばれる
 * 場合、1枚目でサーバーが running になれば2枚目以降は通常どおり即座に成功するため、待機が
 * 発生するのは実質「サーバーが寝ていたときの最初の1回」だけになる。
 *
 * @param options.onWaitingForServer 起動待ちフェーズに入った瞬間に一度だけ呼ばれる
 *   （呼び出し側で「サーバーを起動しています」等のUI表示に使うフック）。
 * @param options.isAborted ポーリングのたびに呼ばれる中断チェック。true を返すと
 *   DOMException('AbortError') を投げて待機を打ち切る（ユーザーによる中断・画面遷移等）。
 */
export async function estimatePoseWithAutoStart(
  image: { base64: string; mimeType: string },
  options: {
    onWaitingForServer?: () => void;
    isAborted?: () => boolean;
  } = {},
): Promise<PoseInferenceResult> {
  try {
    return await generatePoseFromImage(image.base64, image.mimeType);
  } catch (e) {
    // state === 'starting'（supervisor が自動起動を仕掛けた直後）のみ起動待ちに入る。
    // それ以外（not-accepting / offline / 422由来の通常エラー等）はそのまま rethrow する。
    if (!(e instanceof PoseServerUnavailableError) || e.state !== 'starting') {
      throw e;
    }
  }

  options.onWaitingForServer?.();

  const startedAt = Date.now();
  for (;;) {
    if (options.isAborted?.()) {
      throw new DOMException('中断されました', 'AbortError');
    }

    await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    if (options.isAborted?.()) {
      throw new DOMException('中断されました', 'AbortError');
    }
    if (Date.now() - startedAt > MAX_WAIT_MS) {
      throw new Error(
        '推論サーバーの起動に時間がかかっています。起動自体は続いているので、数分後にもう一度お試しください。',
      );
    }

    const health = await fetchPoseServerHealth();
    if (!health.accepting || health.state === 'unreachable' || health.state === 'unconfigured') {
      // 待っても解消しない・むしろ悪化した状態への遷移。待ち続けず即座に諦める。
      throw new Error(
        '推論サーバーへの接続状態が変化したため、生成を中断しました。画面を確認してもう一度お試しください。',
      );
    }
    if (health.state === 'running') {
      // 起動完了。もう一度だけ推定を試す（無限ループにしないため再試行は1回のみ）。
      try {
        return await generatePoseFromImage(image.base64, image.mimeType);
      } catch (e) {
        if (e instanceof PoseServerUnavailableError && e.state === 'starting') {
          // running 直後にまた「起動中」で弾かれるレアケース（ヘルスチェックとリクエスト処理の
          // 間のタイミングのずれ等）。ポーリングを続けず、タイムアウトと同じ「起動に時間が
          // かかっている」扱いにする（無限ループ防止のため再試行は1回のみ）。
          throw new Error(
            '推論サーバーの起動に時間がかかっています。起動自体は続いているので、数分後にもう一度お試しください。',
          );
        }
        throw e;
      }
    }
    // state === 'starting' / 'stopping' / 'stopped' などはまだ待つ。次のポーリングへ。
  }
}
